import { useLayoutEffect, useState } from 'react';

import { Z_ENV_BAR_ATTRIBUTE, Z_ENV_BAR_SELECTOR } from '../constants/layout';

const BELOW_MAIN_PAGE_CONTENT_ID = 'belowMainPageContent';
const ZDS_BUILDER_HEADER_ID = 'zds-builder-header';
const DOM_OBSERVER_OPTIONS: MutationObserverInit = {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['id', Z_ENV_BAR_ATTRIBUTE],
};

const getUnifiedNavShadowRoot = (documentRoot: Document = document) =>
  documentRoot.querySelector('z-unified-nav-v2')?.shadowRoot || null;

const getRoots = (documentRoot: Document = document): ParentNode[] => {
  const shadowRoot = getUnifiedNavShadowRoot(documentRoot);
  return shadowRoot ? [documentRoot, shadowRoot] : [documentRoot];
};

const getElementById = (
  id: string,
  shadowRoot: ParentNode | null,
  documentRoot: Document,
) =>
  shadowRoot?.querySelector<HTMLElement>(`#${id}`) ||
  documentRoot.getElementById(id);

const getPanelAnchor = (documentRoot: Document = document) =>
  getElementById(
    BELOW_MAIN_PAGE_CONTENT_ID,
    getUnifiedNavShadowRoot(documentRoot),
    documentRoot,
  );

const getPanelAnchorTop = () => getPanelAnchor()?.getBoundingClientRect().top;

const getVisibleElementBottom = (el: HTMLElement | null | undefined) => {
  if (!el) {
    return 0;
  }

  const rect = el.getBoundingClientRect();
  const height = rect.height || rect.bottom - rect.top;
  const bottom =
    typeof rect.bottom === 'number' ? rect.bottom : rect.top + height;

  if (height <= 0 || bottom <= 0) {
    return 0;
  }

  return Math.round(bottom);
};

const getBuilderHeaderBottom = () =>
  getVisibleElementBottom(document.getElementById(ZDS_BUILDER_HEADER_ID));

const getEnvironmentBarElements = () =>
  getRoots().reduce<HTMLElement[]>(
    (acc, root) => [
      ...acc,
      ...Array.from(root.querySelectorAll<HTMLElement>(Z_ENV_BAR_SELECTOR)),
    ],
    [],
  );

const getEnvironmentBarBottom = () =>
  getEnvironmentBarElements().reduce(
    (maxBottom, el) => Math.max(maxBottom, getVisibleElementBottom(el)),
    0,
  );

const getTopSpacing = () =>
  Math.max(
    0,
    getPanelAnchorTop() ?? 0,
    getBuilderHeaderBottom(),
    getEnvironmentBarBottom(),
  );

const getObservedElements = () => {
  const elements = [
    document.documentElement,
    document.body,
    getPanelAnchor(),
    document.getElementById(ZDS_BUILDER_HEADER_ID),
    ...getEnvironmentBarElements(),
  ].filter((el): el is HTMLElement => !!el);

  return Array.from(new Set(elements));
};

export const useTopSpacing = () => {
  const [topSpacing, setTopSpacing] = useState(() => getTopSpacing());

  useLayoutEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    let mutationRaf: number | null = null;

    function measure() {
      setTopSpacing((prev) => {
        const next = getTopSpacing();
        return prev === next ? prev : next;
      });
    }

    function scheduleSyncFromDom() {
      if (mutationRaf != null) {
        return;
      }

      mutationRaf = requestAnimationFrame(() => {
        mutationRaf = null;
        syncObservedElements();
      });
    }

    const mutationObserver = new MutationObserver(scheduleSyncFromDom);

    function observeDomRoots() {
      mutationObserver.disconnect();
      mutationObserver.observe(document.documentElement, DOM_OBSERVER_OPTIONS);

      const shadowRoot = getUnifiedNavShadowRoot();
      if (shadowRoot) {
        mutationObserver.observe(shadowRoot, DOM_OBSERVER_OPTIONS);
      }
    }

    function syncObservedElements() {
      resizeObserver?.disconnect();
      resizeObserver = new ResizeObserver(measure);
      getObservedElements().forEach((el) => {
        resizeObserver?.observe(el);
      });

      observeDomRoots();
      measure();
    }

    syncObservedElements();
    const initialRaf = requestAnimationFrame(syncObservedElements);

    window.addEventListener('resize', measure);

    return () => {
      cancelAnimationFrame(initialRaf);
      if (mutationRaf != null) {
        cancelAnimationFrame(mutationRaf);
      }
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return topSpacing;
};
